import React from 'react';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { HttpService } from 'core/services/http';
import { Utils } from 'core/utils';
import {
    Box, Container, Typography, Paper, CircularProgress,
    Alert, Chip, Stack, Avatar, Divider, Button
} from '@mui/material';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import SearchIcon from '@mui/icons-material/Search';
import { Link } from 'react-router-dom';

const DECISION_CONFIG = {
    shortlisted: { label: 'Shortlisted', color: 'success' },
    rejected:    { label: 'Rejected',    color: 'error'   },
    hired:       { label: 'Hired',       color: 'primary' },
};
const decisionColor = (d) => DECISION_CONFIG[d]?.color ?? 'default';
const decisionLabel = (d) => DECISION_CONFIG[d]?.label ?? 'Pending';

const AVATAR_COLORS = ['#1976d2', '#388e3c', '#f57c00', '#7b1fa2', '#c62828', '#00838f'];
const nameToColor = (name = '') => {
    let h = 0;
    for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
    return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
};

function JobseekerApplicationsPage() {
    const navigate = useNavigate();

    const { data: applicationsData, isLoading, isError } = useQuery(
        'myApplications',
        async () => {
            const service = new HttpService();
            return await service.get('/job/apply/', null);
        }
    );

    const applications = applicationsData?.results || applicationsData || [];

    if (isLoading) {
        return (
            <Container maxWidth="lg">
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <CircularProgress size={28} />
                </Box>
            </Container>
        );
    }

    if (isError) {
        return (
            <Container maxWidth="lg">
                <Alert severity="error">Failed to load applications. Please try again.</Alert>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg">
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1.5 }}>
                <Box>
                    <Typography variant="h5" fontWeight={700}>My Applications</Typography>
                    <Typography variant="body2" color="text.secondary">
                        {applications.length > 0
                            ? `${applications.length} application${applications.length !== 1 ? 's' : ''} submitted`
                            : 'Track the status of your job applications'}
                    </Typography>
                </Box>
                <Button
                    component={Link}
                    to="/jobs"
                    variant="outlined"
                    startIcon={<SearchIcon />}
                    size="small"
                >
                    Browse Jobs
                </Button>
            </Box>

            {applications.length === 0 ? (
                <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 6, textAlign: 'center' }}>
                    <Box sx={{
                        width: 56, height: 56, borderRadius: '50%',
                        bgcolor: 'grey.100', display: 'flex', alignItems: 'center',
                        justifyContent: 'center', mx: 'auto', mb: 2,
                    }}>
                        <WorkOutlineIcon sx={{ color: 'text.disabled' }} />
                    </Box>
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                        No applications yet
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
                        Start applying to jobs to see them here.
                    </Typography>
                    <Button component={Link} to="/jobs" variant="contained" disableElevation>
                        Browse Jobs
                    </Button>
                </Paper>
            ) : (
                <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
                    {applications.map((app, idx) => {
                        const company = app.job?.company?.title || app.company_title || 'Unknown Company';
                        const jobTitle = app.job?.title || 'Unknown Job';
                        return (
                            <Box key={app.id}>
                                <Box
                                    onClick={() => navigate(`/jobseeker/applications/${app.id}`)}
                                    sx={{
                                        px: 2.5, py: 2,
                                        display: 'flex', alignItems: 'center', gap: 2,
                                        cursor: 'pointer',
                                        '&:hover': { bgcolor: 'action.hover' },
                                        transition: 'background 0.15s',
                                    }}
                                >
                                    <Avatar
                                        variant="rounded"
                                        sx={{
                                            width: 40, height: 40, fontSize: 15, fontWeight: 700,
                                            bgcolor: nameToColor(company), flexShrink: 0,
                                        }}
                                    >
                                        {company.charAt(0).toUpperCase()}
                                    </Avatar>

                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography variant="body2" fontWeight={600} noWrap>
                                            {jobTitle}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" noWrap display="block">
                                            {company}
                                        </Typography>
                                        {app.decision_notes && (
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                display="block"
                                                sx={{ mt: 0.25, fontStyle: 'italic' }}
                                                noWrap
                                            >
                                                {app.decision_notes}
                                            </Typography>
                                        )}
                                    </Box>

                                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexShrink: 0 }}>
                                        <Chip
                                            label={decisionLabel(app.final_decision)}
                                            color={decisionColor(app.final_decision)}
                                            size="small"
                                            sx={{ height: 22, fontSize: 11 }}
                                        />
                                        {app.created_at && (
                                            <Typography
                                                variant="caption"
                                                color="text.disabled"
                                                sx={{ display: { xs: 'none', sm: 'block' }, whiteSpace: 'nowrap' }}
                                            >
                                                {Utils.timeAgo(app.created_at)}
                                            </Typography>
                                        )}
                                    </Stack>
                                </Box>
                                {idx < applications.length - 1 && <Divider />}
                            </Box>
                        );
                    })}
                </Paper>
            )}
        </Container>
    );
}

export default JobseekerApplicationsPage;
